// Rich, SEO-structured content for the 6 service-category detail pages.
// Pairs with `serviceCategories` in ./services.js (matched by `id`) for the
// list of individual services rendered under "What this includes".
import { IMAGES } from "@/data/site";

export const serviceDetails = [
  {
    id: "diagnosis",
    icon: "Stethoscope",
    image: IMAGES.gen.tropDiagnosis,
    slug: { el: "nevrologiki-exetasi", en: "neurological-examination" },
    metaTitle: {
      el: "Νευρολογική Εξέταση & Διάγνωση — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Neurological Examination & Diagnosis — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Πλήρης νευρολογική αξιολόγηση για πρώτη επίσκεψη ή παρακολούθηση — στο ιατρείο, κατ' οίκον ή μέσω τηλενευρολογίας, στην Αθήνα.",
      en: "Complete neurological assessment for a first visit or follow-up — in the office, at home or via teleneurology, in Athens.",
    },
    hero: {
      eyebrow: { el: "ΥΠΗΡΕΣΙΑ", en: "Service" },
      title: { el: "Νευρολογική Εξέταση & Διάγνωση", en: "Neurological Examination & Diagnosis" },
      lead: {
        el: "Η βάση κάθε φροντίδας: μια προσεκτική, χωρίς βιασύνη κλινική αξιολόγηση που οδηγεί σε σαφή διάγνωση και ξεκάθαρο πλάνο.",
        en: "The foundation of every care plan: a careful, unhurried clinical assessment that leads to a clear diagnosis and a clear plan.",
      },
    },
    overview: {
      el: [
        "Η νευρολογική εξέταση είναι η βασική αξιολόγηση κατά την οποία εκτιμάται συνολικά η λειτουργία του νευρικού συστήματος — από τη μνήμη και την ομιλία έως την ισορροπία, τη δύναμη και τις αισθήσεις. Αποτελεί το πρώτο και πιο κρίσιμο βήμα για τη σωστή διάγνωση.",
        "Κάθε επίσκεψη ξεκινά με λεπτομερές ιστορικό και συνεχίζεται με εξατομικευμένη κλινική εξέταση ανάλογα με το σύμπτωμα ή την πάθηση, ερμηνεία των απεικονιστικών εξετάσεων και σχεδιασμό θεραπευτικού πλάνου. Στο τέλος παραδίδεται έντυπη, ολοκληρωμένη σύνοψη της επίσκεψης.",
      ],
      en: [
        "The neurological examination is the core assessment of how the nervous system is working — from memory and speech to balance, strength and sensation. It is the first and most important step towards an accurate diagnosis.",
        "Every visit begins with a detailed history and continues with an individualised clinical examination according to the symptom or condition, interpretation of imaging and a treatment plan. At the end, a printed, complete summary of the visit is provided.",
      ],
    },
    whoFor: {
      el: [
        "Νέα συμπτώματα όπως κεφαλαλγία, ζάλη, μούδιασμα, αδυναμία ή διαταραχές μνήμης",
        "Παρακολούθηση γνωστής νευρολογικής πάθησης",
        "Ανάγκη για δεύτερη γνώμη σε υπάρχουσα διάγνωση ή θεραπεία",
        "Ασθενείς που δυσκολεύονται να μετακινηθούν (κατ' οίκον εξέταση)",
        "Παρακολούθηση ή αξιολόγηση εξ αποστάσεως μέσω βιντεοκλήσης",
      ],
      en: [
        "New symptoms such as headache, dizziness, numbness, weakness or memory changes",
        "Follow-up of a known neurological condition",
        "The need for a second opinion on an existing diagnosis or treatment",
        "Patients who find it difficult to travel (home visit)",
        "Remote follow-up or assessment via video call",
      ],
    },
    steps: [
      {
        title: { el: "Ιστορικό", en: "History" },
        text: { el: "Αναλυτική συζήτηση για τα συμπτώματα, το ιατρικό ιστορικό και την καθημερινότητά σας.", en: "A thorough discussion of your symptoms, medical history and daily life." },
      },
      {
        title: { el: "Κλινική εξέταση", en: "Clinical examination" },
        text: { el: "Στοχευμένη νευρολογική εξέταση, προσαρμοσμένη στο σύμπτωμα ή την πάθηση.", en: "A targeted neurological examination, tailored to the symptom or condition." },
      },
      {
        title: { el: "Ανάλυση εξετάσεων", en: "Review of tests" },
        text: { el: "Ερμηνεία απεικονιστικών (MRI, CT) και εργαστηριακών εξετάσεων στο πλαίσιο της κλινικής εικόνας.", en: "Interpretation of imaging (MRI, CT) and laboratory tests in the context of the clinical picture." },
      },
      {
        title: { el: "Πλάνο & σύνοψη", en: "Plan & summary" },
        text: { el: "Σχεδιασμός θεραπείας, συνταγογράφηση όπου χρειάζεται και έντυπη σύνοψη της επίσκεψης.", en: "A treatment plan, prescription where required and a printed summary of the visit." },
      },
    ],
    faq: [
      {
        q: { el: "Τι πρέπει να φέρω στην πρώτη μου επίσκεψη;", en: "What should I bring to my first visit?" },
        a: {
          el: "Φέρτε προηγούμενες εξετάσεις (απεικονιστικές, εργαστηριακές και CD εξετάσεων), τη λίστα των φαρμάκων σας και τυχόν προηγούμενες ιατρικές γνωματεύσεις. Βοηθά επίσης η παρουσία συνοδού όταν υπάρχουν διαταραχές μνήμης.",
          en: "Please bring any previous tests (imaging, laboratory reports and test CDs), a list of your medication and any prior medical reports. When there are memory concerns, it also helps to bring a companion.",
        },
      },
      {
        q: { el: "Η εξέταση με βιντεοκλήση είναι εξίσου αξιόπιστη;", en: "Is a video consultation just as reliable?" },
        a: {
          el: "Για παρακολούθηση, αξιολόγηση αποτελεσμάτων και επανασυνταγογράφηση, η τηλενευρολογία είναι κατάλληλη και αποτελεσματική. Ορισμένες περιπτώσεις χρειάζονται φυσική εξέταση — αυτό θα εκτιμηθεί κατά περίπτωση.",
          en: "For follow-up, review of results and prescription renewal, teleneurology is suitable and effective. Some cases need an in-person examination — this is assessed case by case.",
        },
      },
      {
        q: { el: "Η επανεκτίμηση έχει χρέωση;", en: "Is a follow-up assessment charged?" },
        a: {
          el: "Η επανεκτίμηση μπορεί να έχει χρέωση, ανάλογα με τον χρόνο που θα αφιερωθεί και την απόσταση από το προηγούμενο ραντεβού.",
          en: "A follow-up assessment may be charged depending on the time required and the interval since the previous appointment.",
        },
      },
      {
        q: { el: "Προσφέρεται κατ' οίκον εξέταση;", en: "Is a home visit available?" },
        a: {
          el: "Ναι. Η κατ' οίκον νευρολογική εξέταση απευθύνεται σε ασθενείς με κινητικά προβλήματα, προχωρημένη ηλικία ή μετά από εγκεφαλικό, και παρέχει πλήρη αξιολόγηση προσαρμοσμένη στον χώρο τους.",
          en: "Yes. The home neurological visit is intended for patients with mobility difficulties, advanced age or after a stroke, and provides a full assessment adapted to their setting.",
        },
      },
      {
        q: { el: "Πόσο διαρκεί μια πρώτη επίσκεψη;", en: "How long does a first visit take?" },
        a: {
          el: "Η πρώτη επίσκεψη διαρκεί 30 λεπτά, με επαρκή χρόνο για λεπτομερές ιστορικό, εξέταση και σχεδιασμό του πλάνου.",
          en: "A first visit lasts 30 minutes, with time for a detailed history, examination and planning.",
        },
      },
      {
        q: { el: "Θα λάβω γραπτή γνωμάτευση;", en: "Will I receive a written report?" },
        a: {
          el: "Ναι. Στο τέλος της επίσκεψης παραδίδεται έντυπη σύνοψη με τα ευρήματα, τη διάγνωση και το θεραπευτικό πλάνο.",
          en: "Yes. At the end of the visit you receive a printed summary with the findings, diagnosis and treatment plan.",
        },
      },
    ],
  },
  {
    id: "interventional",
    icon: "Syringe",
    image: IMAGES.gen.tropInterventional,
    slug: { el: "epemvatikes-therapies", en: "interventional-treatments" },
    metaTitle: {
      el: "Θεραπείες με Botox & Αποκλεισμός Νεύρων — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Botox Treatments & Nerve Blocks — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Θεραπευτική χορήγηση βοτουλινικής τοξίνης (Botox/Dysport) για σπαστικότητα, δυστονία, σπασμό ημιπροσώπου και χρόνια ημικρανία, καθώς και αποκλεισμός ινιακού νεύρου, με υπερηχογραφική καθοδήγηση όπου χρειάζεται.",
      en: "Therapeutic botulinum toxin (Botox/Dysport) for spasticity, dystonia, hemifacial spasm and chronic migraine, plus occipital nerve block, with ultrasound guidance where required.",
    },
    hero: {
      eyebrow: { el: "ΥΠΗΡΕΣΙΑ", en: "Service" },
      title: { el: "Επεμβατικές & Εξειδικευμένες Θεραπείες", en: "Interventional & Specialist Treatments" },
      lead: {
        el: "Στοχευμένες, τεκμηριωμένες θεραπείες με βοτουλινική τοξίνη και αποκλεισμό νεύρων, για παθήσεις που δεν ανταποκρίνονται επαρκώς στη συμβατική αγωγή.",
        en: "Targeted, evidence-based treatments with botulinum toxin and nerve blocks, for conditions that do not respond adequately to standard medication.",
      },
    },
    overview: {
      el: [
        "Η βοτουλινική τοξίνη (Botox / Dysport) είναι μια καθιερωμένη θεραπευτική επιλογή στη νευρολογία. Χορηγείται σε στοχευμένα σημεία για να μειώσει την υπερβολική μυϊκή δραστηριότητα ή τον πόνο, με προσωρινή και ελεγχόμενη δράση.",
        "Χρησιμοποιείται στη σπαστικότητα μετά από εγκεφαλικό ή κάκωση, σε εστιακές δυστονίες, στον σπασμό ημιπροσώπου και στη χρόνια ημικρανία. Παράλληλα, ο αποκλεισμός ινιακού νεύρου προσφέρει ανακούφιση σε επιλεγμένες κεφαλαλγίες και νευραλγίες. Όπου απαιτείται μεγαλύτερη ακρίβεια, η έγχυση γίνεται με υπερηχογραφική καθοδήγηση.",
      ],
      en: [
        "Botulinum toxin (Botox / Dysport) is an established therapeutic option in neurology. It is delivered to targeted points to reduce excessive muscle activity or pain, with a temporary and controlled effect.",
        "It is used for spasticity after stroke or injury, for focal dystonias, for hemifacial spasm and for chronic migraine. Alongside this, an occipital nerve block offers relief for selected headaches and neuralgias. Where greater precision is needed, the injection is performed under ultrasound guidance.",
      ],
    },
    whoFor: {
      el: [
        "Σπαστικότητα μετά από εγκεφαλικό, κρανιοεγκεφαλική κάκωση ή εγκεφαλική παράλυση",
        "Εστιακές δυστονίες, όπως βλεφαρόσπασμος ή αυχενική δυστονία",
        "Σπασμός ημιπροσώπου",
        "Χρόνια ημικρανία (πονοκέφαλος 15+ ημέρες τον μήνα)",
        "Κεφαλαλγίες ή νευραλγίες κατάλληλες για αποκλεισμό ινιακού νεύρου",
      ],
      en: [
        "Spasticity after stroke, traumatic brain injury or cerebral palsy",
        "Focal dystonias, such as blepharospasm or cervical dystonia",
        "Hemifacial spasm",
        "Chronic migraine (headache on 15+ days per month)",
        "Headaches or neuralgias suitable for an occipital nerve block",
      ],
    },
    steps: [
      {
        title: { el: "Αξιολόγηση", en: "Assessment" },
        text: { el: "Κλινική εκτίμηση του προτύπου σπαστικότητας, δυστονίας ή κεφαλαλγίας και της καταλληλότητας για τη θεραπεία.", en: "Clinical assessment of the spasticity, dystonia or headache pattern and eligibility for treatment." },
      },
      {
        title: { el: "Σχεδιασμός", en: "Planning" },
        text: { el: "Επιλογή των μυϊκών ομάδων ή σημείων-στόχων, με υπερηχογραφική καθοδήγηση όπου χρειάζεται.", en: "Selection of the target muscle groups or points, with ultrasound guidance where required." },
      },
      {
        title: { el: "Χορήγηση", en: "Administration" },
        text: { el: "Η έγχυση γίνεται σύμφωνα με το καθιερωμένο πρωτόκολλο, σε λίγα λεπτά, στο ιατρείο.", en: "The injection is given per the established protocol, in a few minutes, in the office." },
      },
      {
        title: { el: "Επανεκτίμηση", en: "Follow-up" },
        text: { el: "Παρακολούθηση της ανταπόκρισης και προγραμματισμός επανάληψης (συνήθως κάθε 12 εβδομάδες).", en: "Monitoring of the response and scheduling of repeat treatment (usually every 12 weeks)." },
      },
    ],
    faq: [
      {
        q: { el: "Πονάει η θεραπεία με βοτουλινική τοξίνη;", en: "Is botulinum toxin treatment painful?" },
        a: {
          el: "Οι ενέσεις γίνονται με πολύ λεπτές βελόνες και η ενόχληση είναι συνήθως ήπια και σύντομη. Οι περισσότεροι ασθενείς επιστρέφουν αμέσως στις δραστηριότητές τους.",
          en: "The injections use very fine needles and any discomfort is usually mild and brief. Most patients return to their activities straight away.",
        },
      },
      {
        q: { el: "Πόσο γρήγορα εμφανίζεται το αποτέλεσμα και πόσο διαρκεί;", en: "How soon does it work and how long does it last?" },
        a: {
          el: "Η δράση εμφανίζεται συνήθως εντός λίγων ημερών έως δύο εβδομάδων και διαρκεί κατά μέσο όρο περίπου τρεις μήνες, γι' αυτό και η θεραπεία επαναλαμβάνεται περιοδικά.",
          en: "The effect usually appears within a few days to two weeks and lasts on average around three months, which is why treatment is repeated periodically.",
        },
      },
      {
        q: { el: "Η θεραπεία με Botox για ημικρανία καλύπτεται;", en: "Is Botox for migraine covered?" },
        a: {
          el: "Η θεραπεία της χρόνιας ημικρανίας με βοτουλινική τοξίνη είναι εγκεκριμένη και, υπό προϋποθέσεις, μπορεί να αποζημιώνεται. Η καταλληλότητα εκτιμάται με βάση το ιστορικό και τη συχνότητα των κρίσεων.",
          en: "Botulinum toxin treatment for chronic migraine is approved and, under certain conditions, may be reimbursed. Eligibility is assessed based on history and attack frequency.",
        },
      },
      {
        q: { el: "Μπορώ να επιστρέψω αμέσως στις δραστηριότητές μου;", en: "Can I return to my activities right away?" },
        a: {
          el: "Στις περισσότερες περιπτώσεις ναι. Θα λάβετε συγκεκριμένες οδηγίες ανάλογα με τη θεραπεία και την περιοχή που αντιμετωπίστηκε.",
          en: "In most cases yes. You will receive specific instructions depending on the treatment and the area treated.",
        },
      },
      {
        q: { el: "Τι είναι ο αποκλεισμός ινιακού νεύρου;", en: "What is an occipital nerve block?" },
        a: {
          el: "Είναι μια στοχευμένη ένεση κοντά στα ινιακά νεύρα που μπορεί να ανακουφίσει επιλεγμένες κεφαλαλγίες και νευραλγίες, σε λίγα λεπτά στο ιατρείο.",
          en: "It is a targeted injection near the occipital nerves that can relieve selected headaches and neuralgias, in a few minutes in the office.",
        },
      },
    ],
  },
  {
    id: "neuromodulation",
    icon: "Activity",
    image: IMAGES.gen.tropVns,
    slug: { el: "rythmisi-vns", en: "vns-management" },
    metaTitle: {
      el: "Ρύθμιση Νευροδιεγέρτη VNS στην Επιληψία — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "VNS Neurostimulator Management in Epilepsy — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Ρύθμιση και παρακολούθηση νευροδιεγέρτη πνευμονογαστρικού (VNS) σε ασθενείς με φαρμακοανθεκτική επιληψία, σε συνεργασία με τον θεράποντα νευροχειρουργό.",
      en: "Adjustment and monitoring of the Vagus Nerve Stimulator (VNS) in patients with drug-resistant epilepsy, in collaboration with the treating neurosurgeon.",
    },
    hero: {
      eyebrow: { el: "ΥΠΗΡΕΣΙΑ", en: "Service" },
      title: { el: "Ρύθμιση Νευροδιεγερτών (VNS)", en: "Neurostimulator Management (VNS)" },
      lead: {
        el: "Ρύθμιση και κλινική παρακολούθηση συσκευών διεγέρτη πνευμονογαστρικού νεύρου (VNS) σε ασθενείς με φαρμακοανθεκτική επιληψία.",
        en: "Specialist adjustment of the VNS device for patients with drug-resistant epilepsy, aiming for better seizure control.",
      },
    },
    overview: {
      el: [
        "Ο νευροδιεγέρτης του πνευμονογαστρικού νεύρου (VNS) είναι μια θεραπευτική επιλογή για ασθενείς με φαρμακοανθεκτική επιληψία, όταν οι κρίσεις δεν ελέγχονται επαρκώς με φαρμακευτική αγωγή. Η συσκευή τοποθετείται χειρουργικά από νευροχειρουργό.",
        "Μετά την τοποθέτηση, ο νευρολόγος αναλαμβάνει την περιοδική ρύθμιση των παραμέτρων της συσκευής και την παρακολούθηση της ανταπόκρισης. Η σταδιακή, προσεκτική προσαρμογή είναι καθοριστική για τη μεγιστοποίηση του οφέλους με την καλύτερη δυνατή ανοχή.",
      ],
      en: [
        "The Vagus Nerve Stimulator (VNS) is a treatment option for patients with drug-resistant epilepsy, when seizures are not adequately controlled with medication. The device is implanted surgically by a neurosurgeon.",
        "After implantation, the neurologist takes over the periodic adjustment of the device parameters and the monitoring of the response. Gradual, careful tuning is decisive for maximising the benefit with the best possible tolerance.",
      ],
    },
    whoFor: {
      el: [
        "Ασθενείς με φαρμακοανθεκτική επιληψία που έχουν ήδη VNS",
        "Ασθενείς που χρειάζονται επαναρρύθμιση των παραμέτρων της συσκευής",
        "Παρακολούθηση της κλινικής ανταπόκρισης μετά την τοποθέτηση",
      ],
      en: [
        "Patients with drug-resistant epilepsy who already have a VNS",
        "Patients who need re-adjustment of the device parameters",
        "Monitoring of the clinical response after implantation",
      ],
    },
    steps: [
      {
        title: { el: "Αξιολόγηση", en: "Assessment" },
        text: { el: "Εκτίμηση της συχνότητας των κρίσεων και της τρέχουσας ρύθμισης της συσκευής.", en: "Evaluation of seizure frequency and the current device settings." },
      },
      {
        title: { el: "Ρύθμιση", en: "Adjustment" },
        text: { el: "Σταδιακή προσαρμογή των παραμέτρων διέγερσης, με βάση την ανταπόκριση και την ανοχή.", en: "Gradual adjustment of the stimulation parameters, based on response and tolerance." },
      },
      {
        title: { el: "Παρακολούθηση", en: "Monitoring" },
        text: { el: "Τακτική επανεκτίμηση και συνεργασία με τον θεράποντα νευροχειρουργό.", en: "Regular reassessment and collaboration with the treating neurosurgeon." },
      },
    ],
    faq: [
      {
        q: { el: "Το VNS αντικαθιστά τα φάρμακα;", en: "Does the VNS replace medication?" },
        a: {
          el: "Όχι. Το VNS λειτουργεί συμπληρωματικά προς την αντιεπιληπτική αγωγή, με στόχο τη μείωση της συχνότητας και της έντασης των κρίσεων. Η αγωγή προσαρμόζεται ανάλογα με την ανταπόκριση.",
          en: "No. The VNS works alongside anti-seizure medication, aiming to reduce the frequency and intensity of seizures. Medication is adjusted according to the response.",
        },
      },
      {
        q: { el: "Πόσο συχνά χρειάζεται ρύθμιση η συσκευή;", en: "How often does the device need adjustment?" },
        a: {
          el: "Στην αρχή η ρύθμιση γίνεται πιο συχνά, μέχρι να επιτευχθεί η βέλτιστη ισορροπία. Στη συνέχεια οι επισκέψεις αραιώνουν, ανάλογα με την πορεία.",
          en: "Initially, adjustment is more frequent, until the optimal balance is achieved. Afterwards, visits become less frequent, depending on the course.",
        },
      },
      {
        q: { el: "Τοποθετείτε εσείς τη συσκευή VNS;", en: "Do you implant the VNS device?" },
        a: {
          el: "Η τοποθέτηση γίνεται χειρουργικά από νευροχειρουργό. Ο νευρολόγος αναλαμβάνει τη ρύθμιση των παραμέτρων και την παρακολούθηση.",
          en: "Implantation is performed surgically by a neurosurgeon. The neurologist handles the parameter adjustment and monitoring.",
        },
      },
      {
        q: { el: "Πονάει η ρύθμιση της συσκευής;", en: "Does adjusting the device hurt?" },
        a: {
          el: "Η ρύθμιση γίνεται εξωτερικά, χωρίς επέμβαση, και είναι γενικά ανώδυνη. Ορισμένοι νιώθουν μια παροδική αίσθηση κατά τη διέγερση.",
          en: "Adjustment is done externally, without surgery, and is generally painless. Some feel a transient sensation during stimulation.",
        },
      },
      {
        q: { el: "Πόσο σύντομα φαίνεται αποτέλεσμα;", en: "How soon are results seen?" },
        a: {
          el: "Το όφελος εμφανίζεται συνήθως σταδιακά, καθώς η συσκευή ρυθμίζεται στις κατάλληλες παραμέτρους στη διάρκεια αρκετών μηνών.",
          en: "Benefit usually appears gradually, as the device is tuned to the right parameters over several months.",
        },
      },
      {
        q: { el: "Είναι ασφαλής η θεραπεία με VNS;", en: "Is VNS treatment safe?" },
        a: {
          el: "Η VNS είναι μια καθιερωμένη θεραπεία με καλό προφίλ ασφάλειας. Τυχόν ανεπιθύμητες ενέργειες είναι συνήθως ήπιες και σχετίζονται με τη διέγερση.",
          en: "VNS is an established treatment with a good safety profile. Any side effects are usually mild and related to the stimulation.",
        },
      },
    ],
  },
  {
    id: "eeg",
    icon: "Waves",
    image: IMAGES.gen.tropEeg,
    slug: { el: "ilektroegkefalografima", en: "eeg-electroencephalogram" },
    metaTitle: {
      el: "Ηλεκτροεγκεφαλογράφημα (ΗΕΓ) & Ερμηνεία — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "EEG (Electroencephalogram) & Interpretation — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Διενέργεια ηλεκτροεγκεφαλογραφήματος (ΗΕΓ) σε συνεργαζόμενα κέντρα και εξειδικευμένη ερμηνεία, συμπεριλαμβανομένων μακράς διάρκειας Video-ΗΕΓ, από πιστοποιημένη νευρολόγο στην Αθήνα.",
      en: "Electroencephalogram (EEG) performed at partner centres and expert interpretation, including long-term Video-EEG, by a certified neurologist in Athens.",
    },
    hero: {
      eyebrow: { el: "ΥΠΗΡΕΣΙΑ", en: "Service" },
      title: { el: "Ηλεκτροδιαγνωστικός Έλεγχος (ΗΕΓ)", en: "Electrodiagnostic Testing (EEG)" },
      lead: {
        el: "Καταγραφή και εξειδικευμένη ερμηνεία της ηλεκτρικής δραστηριότητας του εγκεφάλου, καθοριστική στη διερεύνηση της επιληψίας.",
        en: "Recording and expert interpretation of the brain's electrical activity — decisive in the investigation of epilepsy.",
      },
    },
    overview: {
      el: [
        "Το ηλεκτροεγκεφαλογράφημα (ΗΕΓ) καταγράφει την ηλεκτρική δραστηριότητα του εγκεφάλου μέσω ηλεκτροδίων στο τριχωτό της κεφαλής. Είναι μια ανώδυνη και ασφαλής εξέταση, θεμελιώδης στη διερεύνηση της επιληψίας, των λιποθυμικών επεισοδίων και άλλων νευρολογικών καταστάσεων.",
        "Η εξέταση διενεργείται σε συνεργαζόμενα διαγνωστικά κέντρα, ενώ η ερμηνεία των ευρημάτων γίνεται εξειδικευμένα, στο πλαίσιο της κλινικής εικόνας του ασθενούς. Η Δρ. Αποστολακοπούλου, με πιστοποίηση σε προηγμένο ΗΕΓ (ILAE), ερμηνεύει και σύνθετες καταγραφές, συμπεριλαμβανομένων των μακράς διάρκειας Video-ΗΕΓ.",
      ],
      en: [
        "The electroencephalogram (EEG) records the brain's electrical activity through electrodes on the scalp. It is a painless and safe test, fundamental to the investigation of epilepsy, syncopal (fainting) episodes and other neurological conditions.",
        "The test is carried out at partner diagnostic centres, while the findings are interpreted expertly, in the context of the patient's clinical picture. Dr. Apostolakopoulou, with advanced EEG certification (ILAE), also interprets complex recordings, including long-term Video-EEG.",
      ],
    },
    whoFor: {
      el: [
        "Διερεύνηση επιληψίας ή μετά από επιληπτική κρίση",
        "Λιποθυμικά επεισόδια ή επεισόδια απώλειας συνείδησης αδιευκρίνιστης αιτίας",
        "Παρακολούθηση γνωστής επιληψίας και εκτίμηση της αγωγής",
        "Ανάγκη εξειδικευμένης ερμηνείας ή δεύτερης γνώμης σε υπάρχον ΗΕΓ",
      ],
      en: [
        "Investigation of epilepsy or after a seizure",
        "Fainting episodes or loss-of-consciousness of unclear cause",
        "Monitoring of known epilepsy and assessment of treatment",
        "The need for expert interpretation or a second opinion on an existing EEG",
      ],
    },
    steps: [
      {
        title: { el: "Παραπομπή", en: "Referral" },
        text: { el: "Καθορισμός του κατάλληλου τύπου ΗΕΓ ανάλογα με το κλινικό ερώτημα και συντονισμός της εξέτασης.", en: "Determining the appropriate type of EEG for the clinical question and coordinating the test." },
      },
      {
        title: { el: "Καταγραφή", en: "Recording" },
        text: { el: "Η εξέταση διενεργείται σε συνεργαζόμενο κέντρο, ανώδυνα και με ασφάλεια.", en: "The test is carried out at a partner centre, painlessly and safely." },
      },
      {
        title: { el: "Ερμηνεία", en: "Interpretation" },
        text: { el: "Λεπτομερής ανάλυση της καταγραφής και συσχέτιση με το ιστορικό.", en: "Detailed analysis of the recording and correlation with the history." },
      },
      {
        title: { el: "Ενσωμάτωση", en: "Integration" },
        text: { el: "Ένταξη των ευρημάτων στο συνολικό διαγνωστικό και θεραπευτικό πλάνο.", en: "Integration of the findings into the overall diagnostic and treatment plan." },
      },
    ],
    faq: [
      {
        q: { el: "Πώς προετοιμάζομαι για ένα ΗΕΓ;", en: "How do I prepare for an EEG?" },
        a: {
          el: "Συνήθως συνιστάται καθαρό τριχωτό της κεφαλής χωρίς προϊόντα, αποφυγή καφεΐνης και, σε ορισμένες περιπτώσεις, ήπια στέρηση ύπνου. Θα λάβετε αναλυτικές οδηγίες ανάλογα με τον τύπο της εξέτασης.",
          en: "Usually clean hair without products, avoiding caffeine and, in some cases, mild sleep deprivation are recommended. You will receive detailed instructions according to the type of test.",
        },
      },
      {
        q: { el: "Είναι επικίνδυνο ή επώδυνο το ΗΕΓ;", en: "Is an EEG dangerous or painful?" },
        a: {
          el: "Όχι. Το ΗΕΓ είναι εντελώς ανώδυνο και ασφαλές. Τα ηλεκτρόδια απλώς καταγράφουν τη δραστηριότητα του εγκεφάλου· δεν χορηγείται κανένα ρεύμα.",
          en: "No. The EEG is completely painless and safe. The electrodes simply record the brain's activity; no current is delivered.",
        },
      },
      {
        q: { el: "Πόσο διαρκεί ένα ΗΕΓ;", en: "How long does an EEG take?" },
        a: {
          el: "Το ραντεβού για ένα τυπικό ΗΕΓ διαρκεί 60 λεπτά. Οι μακράς διάρκειας ή Video-ΗΕΓ καταγραφές διαρκούν περισσότερο.",
          en: "An appointment for a standard EEG lasts 60 minutes. Long-term or Video-EEG recordings take longer.",
        },
      },
      {
        q: { el: "Τι είναι το Video-ΗΕΓ;", en: "What is Video-EEG?" },
        a: {
          el: "Είναι μια ταυτόχρονη καταγραφή ΗΕΓ και βίντεο, που βοηθά να συσχετιστούν τα επεισόδια με την εγκεφαλική δραστηριότητα.",
          en: "It is a simultaneous EEG and video recording that helps correlate episodes with brain activity.",
        },
      },
      {
        q: { el: "Μπορείτε να ερμηνεύσετε ΗΕΓ που έγινε αλλού;", en: "Can you interpret an EEG done elsewhere?" },
        a: {
          el: "Ναι. Προσφέρεται εξειδικευμένη ερμηνεία και δεύτερη γνώμη σε υπάρχουσες καταγραφές ΗΕΓ.",
          en: "Yes. Expert interpretation and a second opinion on existing EEG recordings are available.",
        },
      },
      {
        q: { el: "Πρέπει να διακόψω τα φάρμακά μου πριν το ΗΕΓ;", en: "Should I stop my medication before an EEG?" },
        a: {
          el: "Όχι. Μη διακόπτετε φάρμακα χωρίς οδηγία· θα λάβετε συγκεκριμένες οδηγίες προετοιμασίας ανάλογα με τον σκοπό της εξέτασης.",
          en: "Usually no. Do not stop medication without instruction; you will receive specific preparation instructions according to the purpose of the test.",
        },
      },
    ],
  },
  {
    id: "diagnostics-visits",
    icon: "ClipboardCheck",
    image: IMAGES.gen.tropVisits,
    slug: { el: "eidikes-episkepseis", en: "specialised-visits" },
    metaTitle: {
      el: "Έλεγχος Μνήμης & Ειδικές Επισκέψεις — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Memory Check-up & Specialised Visits — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Ετήσιος έλεγχος μνήμης με νευροψυχολογικό screening, επαναληπτικές επισκέψεις μέσω βιντεοκλήσης και ιατρικές βεβαιώσεις.",
      en: "Annual memory check-up with cognitive screening, follow-up video visits and medical certificates.",
    },
    hero: {
      eyebrow: { el: "ΥΠΗΡΕΣΙΑ", en: "Service" },
      title: { el: "Έλεγχος Μνήμης & Ειδικές Επισκέψεις", en: "Memory Check-up & Specialised Visits" },
      lead: {
        el: "Στοχευμένες επισκέψεις για πρόληψη και παρακολούθηση — από τον ετήσιο έλεγχο μνήμης έως τις επαναληπτικές επισκέψεις και τις ιατρικές βεβαιώσεις.",
        en: "Focused visits for prevention and follow-up — from the annual memory check-up to follow-up visits and medical certificates.",
      },
    },
    overview: {
      el: [
        "Πέρα από την κλασική νευρολογική εξέταση, ορισμένες ανάγκες εξυπηρετούνται καλύτερα με στοχευμένες, ειδικές επισκέψεις. Ο ετήσιος έλεγχος μνήμης και οι επαναληπτικές επισκέψεις μέσω βιντεοκλήσης διευκολύνουν την παρακολούθηση χωρίς περιττές μετακινήσεις.",
        "Ο ετήσιος έλεγχος άνοιας / Alzheimer συνδυάζει τη νευρολογική εκτίμηση με δομημένο τεστ μνήμης, ώστε να παρακολουθείται η πορεία στον χρόνο και να αναγνωρίζονται έγκαιρα τυχόν αλλαγές. Εκδίδονται επίσης ιατρικές βεβαιώσεις κατά περίπτωση.",
      ],
      en: [
        "Beyond the classic neurological examination, some needs are better served by focused, specialised visits. The annual memory check-up and follow-up video visits make monitoring easier without unnecessary travel.",
        "The annual dementia / Alzheimer's check-up combines neurological assessment with a structured memory test, so that the course can be monitored over time and any changes recognised early. Medical certificates are also issued as required.",
      ],
    },
    whoFor: {
      el: [
        "Προληπτικός ετήσιος έλεγχος μνήμης (συχνά μετά τα 60 ή με οικογενειακό ιστορικό)",
        "Γνωστοί ασθενείς που χρειάζονται παρακολούθηση εξ αποστάσεως",
        "Ανάγκη έκδοσης ιατρικής βεβαίωσης",
      ],
      en: [
        "A preventive annual memory check (often after 60 or with a family history)",
        "Existing patients who need remote follow-up",
        "The need for a medical certificate",
      ],
    },
    steps: [
      {
        title: { el: "Επιλογή επίσκεψης", en: "Choose the visit" },
        text: { el: "Επιλέγετε τον τύπο επίσκεψης που ταιριάζει στην ανάγκη σας — έλεγχος, παρακολούθηση ή συνταγογράφηση.", en: "You choose the type of visit that fits your need — check-up, follow-up or prescription." },
      },
      {
        title: { el: "Αξιολόγηση", en: "Assessment" },
        text: { el: "Νευρολογική εκτίμηση και, όπου προβλέπεται, νευροψυχολογικό screening μνήμης.", en: "Neurological assessment and, where applicable, a memory (neuropsychological) screening." },
      },
      {
        title: { el: "Ολοκλήρωση", en: "Completion" },
        text: { el: "Βεβαίωση ή πλάνο παρακολούθησης, ανάλογα με την περίπτωση.", en: "A certificate or a follow-up plan, depending on the case." },
      },
    ],
    faq: [
      {
        q: { el: "Τι περιλαμβάνει ο ετήσιος έλεγχος μνήμης;", en: "What does the annual memory check-up include?" },
        a: {
          el: "Περιλαμβάνει νευρολογική εξέταση, δομημένο τεστ μνήμης (νευροψυχολογικό screening) και σχεδιασμό παρακολούθησης, ώστε να παρακολουθείται η γνωστική λειτουργία στον χρόνο.",
          en: "It includes a neurological examination, a structured memory test (cognitive screening) and follow-up planning, so that cognitive function can be tracked over time.",
        },
      },
      {
        q: { el: "Σε τι διαφέρει από μια κανονική επίσκεψη;", en: "How is this different from a regular visit?" },
        a: {
          el: "Οι ειδικές επισκέψεις είναι στοχευμένες σε μια συγκεκριμένη ανάγκη — έλεγχο μνήμης, παρακολούθηση ή συνταγογράφηση — με ανάλογη δομή και χρόνο.",
          en: "Specialised visits target a specific need — a memory check, follow-up or prescription — with matching structure and time.",
        },
      },
      {
        q: { el: "Εκδίδετε ιατρικές βεβαιώσεις;", en: "Do you issue medical certificates?" },
        a: {
          el: "Ναι, εκδίδονται ιατρικές βεβαιώσεις κατά περίπτωση, εφόσον δικαιολογούνται από την κλινική εκτίμηση.",
          en: "Yes, medical certificates are issued as appropriate, when justified by the clinical assessment.",
        },
      },
      {
        q: { el: "Πόσο συχνά πρέπει να γίνεται ο έλεγχος μνήμης;", en: "How often should the memory check be done?" },
        a: {
          el: "Συνήθως συνιστάται ετήσιος έλεγχος, ώστε να παρακολουθείται η γνωστική λειτουργία στον χρόνο και να αναγνωρίζονται έγκαιρα τυχόν αλλαγές.",
          en: "An annual check is usually recommended, so cognitive function can be tracked over time and any changes recognised early.",
        },
      },
    ],
  },
  {
    id: "prevention",
    icon: "Brain",
    image: IMAGES.gen.tropPrevention,
    slug: { el: "provlipsi-ygeia-egkefalou", en: "brain-health-prevention" },
    metaTitle: {
      el: "Πρόληψη & Υγεία Εγκεφάλου — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Prevention & Brain Health — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Πρόγραμμα Υγείας Εγκεφάλου με αξιολόγηση τροποποιήσιμων παραγόντων κινδύνου και εξατομικευμένες συστάσεις, καθώς και προγράμματα νοητικής ενδυνάμωσης & χαλάρωσης.",
      en: "A Brain Health programme assessing modifiable risk factors with personalised recommendations, plus cognitive-empowerment & relaxation programmes.",
    },
    hero: {
      eyebrow: { el: "ΥΠΗΡΕΣΙΑ", en: "Service" },
      title: { el: "Πρωτόκολλα Πρόληψης & Ολιστικής Φροντίδας", en: "Prevention & Holistic-Care Protocols" },
      lead: {
        el: "Μια προληπτική, ολιστική προσέγγιση για τη διατήρηση της εγκεφαλικής υγείας, πριν εμφανιστούν συμπτώματα.",
        en: "A preventive, holistic approach to maintaining brain health, before symptoms appear.",
      },
    },
    overview: {
      el: [
        "Η υγεία του εγκεφάλου δεν αφορά μόνο τη θεραπεία των παθήσεων, αλλά και την πρόληψή τους. Το Πρωτόκολλο Υγείας Εγκεφάλου εστιάζει στην αναγνώριση και διαχείριση των τροποποιήσιμων παραγόντων κινδύνου — καρδιαγγειακών, μεταβολικών και τρόπου ζωής — που επηρεάζουν τη μακροχρόνια νοητική λειτουργία.",
        "Η προσέγγιση είναι εξατομικευμένη και ανθρωποκεντρική. Παράλληλα αναπτύσσονται προγράμματα νοητικής ενδυνάμωσης και χαλάρωσης, με στόχο την ενίσχυση της μνήμης, της προσοχής και της ψυχικής ισορροπίας.",
      ],
      en: [
        "Brain health is not only about treating conditions, but also about preventing them. The Brain Health Protocol focuses on recognising and managing modifiable risk factors — cardiovascular, metabolic and lifestyle — that influence long-term cognitive function.",
        "The approach is individualised and patient-centred. Cognitive-empowerment and relaxation programmes are also in development, aiming to strengthen memory, attention and mental balance.",
      ],
    },
    whoFor: {
      el: [
        "Άτομα με απαιτητική εργασία που βιώνουν πνευματική κόπωση",
        "Όσοι θέλουν την καλύτερη ισορροπία επαγγελματικής και προσωπικής ζωής",
        "Όσοι επιθυμούν βελτιστοποίηση των νοητικών λειτουργιών και μέγιστη απόδοση",
        "Άτομα με οικογενειακό ιστορικό ή τροποποιήσιμους παράγοντες κινδύνου που θέλουν να προλάβουν τη νοητική έκπτωση",
        "Όσοι επιθυμούν ανάπτυξη ανθεκτικότητας στο στρες μέσω καινοτόμων προσεγγίσεων",
      ],
      en: [
        "People with demanding work who experience mental fatigue",
        "Those seeking a better work–life balance",
        "Those who want to optimise cognitive function and peak performance",
        "People with a family history or modifiable risk factors who want to prevent cognitive decline",
        "Those wishing to build resilience to stress through innovative approaches",
      ],
    },
    steps: [
      {
        title: { el: "Αξιολόγηση κινδύνου", en: "Risk assessment" },
        text: { el: "Εκτίμηση καρδιαγγειακών, μεταβολικών παραγόντων και τρόπου ζωής.", en: "Assessment of cardiovascular, metabolic factors and lifestyle." },
      },
      {
        title: { el: "Εξατομικευμένες συστάσεις", en: "Personalised recommendations" },
        text: { el: "Στοχευμένες συμβουλές για διατροφή, άσκηση, ύπνο και διαχείριση του άγχους.", en: "Targeted advice on diet, exercise, sleep and stress management." },
      },
      {
        title: { el: "Παρακολούθηση", en: "Follow-up" },
        text: { el: "Προγραμματισμός επανεκτίμησης για την παρακολούθηση της προόδου στον χρόνο.", en: "Scheduling of reassessment to track progress over time." },
      },
    ],
    faq: [
      {
        q: { el: "Μπορεί πραγματικά να προληφθεί η νοητική έκπτωση;", en: "Can cognitive decline really be prevented?" },
        a: {
          el: "Δεν υπάρχουν εγγυήσεις, όμως η έγκαιρη διαχείριση τροποποιήσιμων παραγόντων κινδύνου μειώνει τεκμηριωμένα τον κίνδυνο και υποστηρίζει τη μακροχρόνια υγεία του εγκεφάλου.",
          en: "There are no guarantees, but the early management of modifiable risk factors is shown to reduce risk and support long-term brain health.",
        },
      },
      {
        q: { el: "Χρειάζεται να έχω συμπτώματα για να ξεκινήσω;", en: "Do I need symptoms to start?" },
        a: {
          el: "Όχι. Το πρόγραμμα είναι προληπτικό και απευθύνεται και σε άτομα χωρίς συμπτώματα που θέλουν να φροντίσουν προληπτικά την εγκεφαλική τους υγεία.",
          en: "No. The programme is preventive and is also intended for people without symptoms who want to look after their brain health proactively.",
        },
      },
      {
        q: { el: "Τι περιλαμβάνει το Πρωτόκολλο Υγείας Εγκεφάλου;", en: "What does the Brain Health Protocol include?" },
        a: {
          el: "Περιλαμβάνει αξιολόγηση τροποποιήσιμων παραγόντων κινδύνου και εξατομικευμένες συστάσεις για διατροφή, άσκηση, ύπνο και διαχείριση του άγχους.",
          en: "It includes assessment of modifiable risk factors and personalised advice on diet, exercise, sleep and stress management.",
        },
      },
      {
        q: { el: "Πόσο συχνά πρέπει να επαναλαμβάνεται;", en: "How often should it be repeated?" },
        a: {
          el: "Συνήθως συνιστάται ετήσια επανεκτίμηση, ώστε να παρακολουθείται η πρόοδος και να προσαρμόζονται οι συστάσεις.",
          en: "An annual review is usually recommended, to track progress and adapt the recommendations.",
        },
      },
      {
        q: { el: "Είναι διαθέσιμα τα προγράμματα νοητικής ενδυνάμωσης;", en: "Are the cognitive-empowerment programmes available?" },
        a: {
          el: "Τα προγράμματα νοητικής ενδυνάμωσης και χαλάρωσης βρίσκονται υπό ανάπτυξη και θα ανακοινωθούν σύντομα.",
          en: "The cognitive-empowerment and relaxation programmes are in development and will be announced soon.",
        },
      },
      {
        q: { el: "Απευθύνεται και σε νεότερους ενήλικες;", en: "Is it also for younger adults?" },
        a: {
          el: "Ναι. Η πρόληψη έχει αξία σε κάθε ηλικία, ιδίως όταν υπάρχουν παράγοντες κινδύνου ή οικογενειακό ιστορικό.",
          en: "Yes. Prevention is valuable at any age, especially when there are risk factors or a family history.",
        },
      },
    ],
  },
];

export const serviceSlugById = Object.fromEntries(serviceDetails.map((s) => [s.id, s.slug]));

export function findServiceDetail(slug) {
  return serviceDetails.find((s) => s.slug.el === slug || s.slug.en === slug);
}
